import { store } from '../store';

/**
 * Get authentication headers from Redux store or localStorage fallback
 * This replaces localStorage.getItem('auth_token') usage throughout the app
 * 
 * NOTE: Auth tokens are now stored in secure HTTP-only cookies for HIPAA compliance.
 * The Authorization header is kept for backward compatibility.
 * Make sure to use credentials: 'include' in fetch requests to send cookies.
 */
export const getAuthHeaders = (): HeadersInit => {
  const state = store.getState();
  let token = state.auth.token;
  
  // Fallback to localStorage if Redux doesn't have the token
  if (!token) {
    token = localStorage.getItem('auth_token');
  }
  
  // IMPORTANT: If no token is found, return headers WITHOUT Authorization header
  // The backend will use the auth_token cookie instead (HIPAA-compliant)
  if (!token) {
    console.warn('No authentication token found in Redux store or localStorage. Using cookie-based auth.');
    return {
      'Content-Type': 'application/json'
    };
  }
  
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

/**
 * Get authentication headers for file uploads (without Content-Type)
 * This allows the browser to set the correct multipart/form-data Content-Type
 */
export const getAuthHeadersForUpload = () => {
  const state = store.getState();
  let token = state.auth.token;
  
  // Fallback to localStorage if Redux doesn't have the token
  if (!token) {
    token = localStorage.getItem('auth_token');
  }
  
  // IMPORTANT: If no token is found, return empty headers object
  // The backend will use the auth_token cookie instead (HIPAA-compliant)
  if (!token) {
    console.warn('No authentication token found in Redux store or localStorage. Using cookie-based auth.');
    return {};
  }
  
  return {
    'Authorization': `Bearer ${token}`
    // Note: No Content-Type header - let browser set multipart/form-data automatically
  };
};

/**
 * Get auth token from Redux store or localStorage fallback
 * 
 * IMPORTANT: Since we now use HTTP-only cookies for HIPAA compliance,
 * this function should NOT be used for authentication.
 * Use credentials: 'include' in fetch/axios calls instead.
 * 
 * This function is kept for backward compatibility only.
 */
export const getAuthToken = () => {
  const state = store.getState();
  let token = state.auth.token;
  
  // Fallback to localStorage if Redux doesn't have the token
  if (!token) {
    token = localStorage.getItem('auth_token');
  }
  
  if (!token) {
    // Don't throw error - we're using HTTP-only cookie auth now
    console.info('Using HTTP-only cookie authentication (HIPAA-compliant)');
    return null;
  }
  
  return token;
};


